import React from "react";
import { useUser } from "../../../Context/UserContext";
import { useNavigate } from "react-router-dom";
import { Paperclip, FileText, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { toast } from "sonner";

const ModalPostular2 = ({
  show,
  onBack,
  onClose,
  onSubmit,
  tender,
  formData,
  setFormData,
}) => {
  const { respuestas, adjuntosPreguntas } = formData; // ✅ Extraemos desde `formData`

  const { user } = useUser();
  const navigate = useNavigate();

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  const ALLOWED_EXTENSIONS = [
    "jpg",
    "jpeg",
    "png",
    "pdf",
    "docx",
    "doc",
    "xlsx",
    "xls",
    "csv",
    "txt",
    "pptx",
    "ppt",
    "zip",
    "rar",
    "7z",
  ];

  // Parsear preguntas_licitador al inicio del componente
  let preguntas = [];
  try {
    preguntas = Array.isArray(tender?.preguntas_licitador)
      ? tender.preguntas_licitador
      : JSON.parse(tender?.preguntas_licitador || "[]");
  } catch (error) {
    console.error("Error al parsear preguntas_licitador:", error);
  }

  const handleRespuestaChange = (pregunta, respuesta) => {
    setFormData((prev) => ({
      ...prev,
      respuestas: {
        ...prev.respuestas,
        [pregunta]: respuesta || "", // ✅ Siempre asignar un valor válido
      },
    }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    // ✅ Recopilar preguntas sin respuesta
    const preguntasSinResponder = preguntas
      .filter(({ question, type }) => {
        if (type === "Solo archivos adjuntos") {
          return (
            !adjuntosPreguntas[question] &&
            respuestas[question] !== "Sin documento adjunto"
          );
        }
        return !respuestas[question];
      })
      .map(({ question }) => question); // Guardamos solo el texto de la pregunta

    if (preguntasSinResponder.length > 0) {
      toast.warning(
        `⚠️ Las siguientes preguntas no han sido respondidas:\n\n- ${preguntasSinResponder.join(
          "\n- "
        )}\n\nPor favor, completa todas las respuestas antes de continuar.`
      );
      return;
    }

    // ✅ Enviar respuestas y adjuntosPreguntas
    onSubmit();
  };

  const handleFileChangePregunta = (pregunta, e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      return (
        ALLOWED_EXTENSIONS.includes(fileExtension) && file.size <= MAX_FILE_SIZE
      );
    });

    if (validFiles.length > 0) {
      setFormData((prev) => {
        const updatedAdjuntosPreguntas = {
          ...prev.adjuntosPreguntas,
          [pregunta]: {
            pregunta, // ✅ Asegurar que la pregunta se almacena
            archivos: validFiles.slice(0, 1), // ✅ Solo permite UN archivo, reemplaza el anterior
          },
        };

        return {
          ...prev,
          adjuntosPreguntas: updatedAdjuntosPreguntas,
          respuestas: {
            ...prev.respuestas,
            [pregunta]: "", // ✅ Borra cualquier respuesta escrita al adjuntar archivo
          },
        };
      });
    }
  };

  const handleFileRemovePregunta = (pregunta) => {
    setFormData((prev) => {
      const updatedAdjuntosPreguntas = { ...prev.adjuntosPreguntas };
      delete updatedAdjuntosPreguntas[pregunta];

      return {
        ...prev,
        adjuntosPreguntas: updatedAdjuntosPreguntas,
        respuestas: {
          ...prev.respuestas,
          [pregunta]: "", // ✅ Permitir volver a marcar "Sin documento adjunto"
        },
      };
    });
  };

  const handleBackClick = () => {
    onBack(); // ✅ Pasar adjuntosPreguntas también
  };

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-full bg-primary" />
        </div>
        <DialogHeader className="mt-4">
          <DialogTitle>Postular a la Licitación</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          <strong className="text-foreground">Título: </strong> {tender?.titulo || "Sin título"}
        </p>

        {/* Información de contacto */}
        <div className="mt-2 rounded-md border border-border p-3 text-sm">
          <div className="flex items-center justify-between">
            <label className="font-medium text-foreground">Perfil del postulante:</label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(
                  `/profile/${user.user.id_user}/${user.user.nombre}-${user.user.apellido}`
                )
              }
            >
              Editar
            </Button>
          </div>
          <div className="mt-2 space-y-1 text-muted-foreground">
            <p>
              <strong className="text-foreground">Nombre:</strong>{" "}
              {user?.user?.nombre && user?.user?.apellido
                ? `${user.user.nombre} ${user.user.apellido}`
                : "Nombre no disponible"}
            </p>
            <p>
              <strong className="text-foreground">Correo Electrónico:</strong>{" "}
              {user.user.correo || "Correo no disponible"}
            </p>
            <p>
              <strong className="text-foreground">Teléfono:</strong>{" "}
              {user.user.codigo_pais
                ? `${user.user.codigo_pais} - ${user.user.celular}`
                : user.user.celular || "Teléfono no disponible"}
            </p>
            {/* Mostrar empresa solo si user.companyUser existe y su estado_usuario es "activo" */}
            {user.companyUser &&
              user.companyUser.estado_usuario === "activo" && (
                <p>
                  <strong className="text-foreground">Empresa:</strong>{" "}
                  <a
                    href={`/company-profile/${
                      user.companyUser.id_company
                    }/${encodeURIComponent(user.companyInfo.nombre_empresa)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {user.companyInfo.nombre_empresa || "Empresa no registrada"}
                  </a>
                </p>
              )}
          </div>
        </div>

        <p className="mt-4 text-sm font-medium text-foreground">Responde las siguientes preguntas:</p>

        <form onSubmit={handleFormSubmit} className="mt-2 flex flex-col gap-4">
          {/* Preguntas dinámicas con numeración */}
          {preguntas.map(({ question, type }, index) => (
            <div key={index}>
              <p className="text-sm text-foreground">
                {index + 1}. {question || "Pregunta no disponible"}
              </p>
              {type === "Solo permite números" && (
                <Input
                  type="number"
                  className="mt-1.5"
                  value={formData.respuestas?.[question] || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 15) {
                      handleRespuestaChange(question, value);
                    } else {
                      toast.warning("Solo se permiten hasta 15 caracteres.");
                    }
                  }}
                  placeholder="Ingresa un valor numérico"
                  required
                />
              )}
              {type === "Respuesta de Sí o No" && (
                <select
                  className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  value={formData.respuestas?.[question] || ""}
                  onChange={(e) => handleRespuestaChange(question, e.target.value)}
                  required
                >
                  <option value="">Selecciona una opción</option>
                  <option value="Sí">Sí</option>
                  <option value="No">No</option>
                </select>
              )}
              {type === "Pregunta abierta" && (
                <textarea
                  className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  value={formData.respuestas?.[question] || ""}
                  onChange={(e) => handleRespuestaChange(question, e.target.value)}
                  placeholder="Escribe tu respuesta"
                  maxLength={200}
                  rows={3}
                  required
                ></textarea>
              )}
              {/* Tipo: Solo archivos adjuntos */}
              {type === "Solo archivos adjuntos" && (
                <div className="mt-1.5 space-y-2">
                  {/* Opción 1: Adjuntar archivo (solo si "No adjuntar" no está marcado) */}
                  {!formData.respuestas?.[question] && (
                    <label className="flex w-fit cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted">
                      <input
                        type="file"
                        onChange={(e) => handleFileChangePregunta(question, e)}
                        accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                        className="hidden"
                        disabled={
                          formData.respuestas?.[question] === "Sin documento adjunto"
                        } // ✅ Bloquea adjuntar si se marcó "Sin documento adjunto"
                      />
                      <Paperclip className="h-4 w-4" />
                      {adjuntosPreguntas?.[question]?.archivos?.length > 0
                        ? adjuntosPreguntas?.[question].archivos[0].name.length > 15
                          ? adjuntosPreguntas?.[question].archivos[0].name.slice(0, 15) + "..."
                          : adjuntosPreguntas?.[question].archivos[0].name
                        : "Adjuntar archivo"}
                    </label>
                  )}

                  {/* ✅ Mostrar archivos adjuntos con opción de eliminar */}
                  {adjuntosPreguntas?.[question]?.archivos?.length > 0 && (
                    <div>
                      {adjuntosPreguntas?.[question].archivos.map((file, index) => (
                        <div key={index} className="flex items-center justify-between gap-2 text-sm">
                          <a
                            className="flex items-center gap-1.5 text-primary hover:underline"
                            href={file instanceof File ? URL.createObjectURL(file) : file.file_path}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FileText className="h-4 w-4" />
                            {(file.name || file.file_name).length > 30
                              ? (file.name || file.file_name).slice(0, 30) + "..."
                              : file.name || file.file_name}
                          </a>
                          <button
                            type="button"
                            onClick={() => handleFileRemovePregunta(question)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Opción 2: No adjuntar (solo si no hay archivos adjuntos) */}
                  {!adjuntosPreguntas?.[question]?.archivos?.length && (
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                      <input
                        type="checkbox"
                        onChange={() => {
                          if (formData.respuestas?.[question] === "Sin documento adjunto") {
                            handleRespuestaChange(question, ""); // ✅ Permitir volver a adjuntar archivos
                          } else {
                            handleRespuestaChange(question, "Sin documento adjunto"); // ✅ Bloquea adjuntar archivos
                          }
                        }}
                        checked={formData.respuestas?.[question] === "Sin documento adjunto"}
                      />
                      No adjuntar
                    </label>
                  )}
                </div>
              )}
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleBackClick}>
              Atrás
            </Button>
            <Button type="submit">Confirmar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ModalPostular2;
